import React, {useEffect, useRef, useState} from 'react';
import {loadVideoElement} from '@Utils/video.util';
import {v4 as uuidv4} from 'uuid';
import {DOTS_COLOR, ThreeDotsLoader} from '@Components/three-dots-loader/three-dots-loader';
import styles from './trim-video-thumbnail-sprite.module.scss';

export interface TrimVideoThumbnailSpriteProps {
  source: string;
  duration: number;
  height?: number;
  width?: string;
  scaledWidth?: number;
  scaledHeight?: number;
  className?: string;
}

const fallBackWidth = 800;
const THUMBNAIL_MAX_WIDTH = 140;
const EXTRA_FRAMES = 10;

export function TrimVideoThumbnailSprite({scaledWidth = 1, scaledHeight = 1, height = 100, ...props}: TrimVideoThumbnailSpriteProps) {
  const id: string = uuidv4();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const createThumnailSprite = () => {
    const maxContentWidth = containerRef?.current?.offsetWidth ?? fallBackWidth;
    const heightOfCanvas = height;
    const maxWidthOfEachThumbnail = THUMBNAIL_MAX_WIDTH;
    const fullWidthOfEachThumbnail = (scaledWidth / scaledHeight) * heightOfCanvas;
    const widthOfEachThumbnail = fullWidthOfEachThumbnail > maxWidthOfEachThumbnail ? maxWidthOfEachThumbnail : fullWidthOfEachThumbnail;
    const numberOfThumbnails = Math.floor(maxContentWidth / widthOfEachThumbnail);
    let thumbnailIndex = 0;
    let thumbnailsCreated = false;
    const ctx = canvasRef?.current?.getContext('2d');
    const widthToSet = (numberOfThumbnails + EXTRA_FRAMES) * widthOfEachThumbnail;

    if (canvasRef && canvasRef.current) {
      canvasRef.current.width = widthToSet;
      canvasRef.current.height = heightOfCanvas;
    }

    if (containerRef && containerRef.current) {
      if (props.width) {
        containerRef.current.style.width = props.width;
      }
      containerRef.current.style.height = `${heightOfCanvas}px`;
    }

    loadVideoElement(props.source, {})
      .then((videoElement) => {
        onVideoDataLoaded(videoElement, heightOfCanvas);
      })
      .catch((e) => {
        console.error(e);
      });

    const onVideoDataLoaded = (videoElement: HTMLVideoElement, canvasHeight: number) => {
      const {videoWidth} = videoElement;
      const scaledRatio = videoElement.videoHeight / canvasHeight;

      videoElement.addEventListener('seeked', videoSeeked.bind(null, videoElement, videoWidth, scaledRatio), false);
      videoElement.currentTime = 0.01;
    };

    const videoSeeked = (videoElement: HTMLVideoElement, videoWidth: number, scaledRatio: number) => {
      if (!thumbnailsCreated) {
        ctx?.drawImage(
          videoElement,
          (videoWidth - widthOfEachThumbnail * scaledRatio) / 2,
          0,
          widthOfEachThumbnail * scaledRatio,
          heightOfCanvas * scaledRatio,
          thumbnailIndex * widthOfEachThumbnail,
          0,
          widthOfEachThumbnail,
          heightOfCanvas
        );
        ctx?.beginPath();
        ctx?.moveTo(thumbnailIndex * widthOfEachThumbnail, 0);
        ctx?.lineTo(thumbnailIndex * widthOfEachThumbnail, heightOfCanvas);
        ctx?.stroke();
        thumbnailIndex++;
        if (thumbnailIndex <= numberOfThumbnails) {
          videoElement.currentTime = props.duration * (thumbnailIndex / numberOfThumbnails);
        } else {
          // add last frame to canvas
          ctx?.drawImage(
            videoElement,
            (videoWidth - widthOfEachThumbnail * scaledRatio) / 2,
            0,
            widthOfEachThumbnail * scaledRatio,
            heightOfCanvas * scaledRatio,
            thumbnailIndex * widthOfEachThumbnail,
            0,
            widthOfEachThumbnail,
            heightOfCanvas
          );
          ctx?.beginPath();
          ctx?.moveTo(thumbnailIndex * widthOfEachThumbnail, 0);
          ctx?.lineTo(thumbnailIndex * widthOfEachThumbnail, heightOfCanvas);
          ctx?.stroke();
          thumbnailsCreated = true;
          setIsLoading(false);
        }
      }
    };
  };

  useEffect(createThumnailSprite, []);

  return (
    <div ref={containerRef} className={`${styles.container} ${props.className ?? ''} ${isLoading ? 'flex-center' : ''}`}>
      <canvas id={id} ref={canvasRef} style={isLoading ? {display: 'none'} : {display: 'block'}} />
      {isLoading && <ThreeDotsLoader distance={6} size={6} color={DOTS_COLOR.DARK} />}
    </div>
  );
}
